/**
 * Custom hook for managing inventory counts with caching
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSpace } from '../contexts/SpaceContext';
import API_BASE_URL from '../config';
import {
  getCachedCounts,
  setCachedCounts,
  invalidateCountsCache,
} from '../utils/inventoryCache';

interface InventoryCounts {
  all: number;
  wandering: number;
  shared_with_me: number;
  by_collection: Record<string, number>;
}

interface UseInventoryCountsReturn {
  counts: InventoryCounts | null;
  loading: boolean;
  error: string | null;
  refetchCounts: () => Promise<void>;
  invalidateCounts: () => void;
}

export const useInventoryCounts = (refreshTrigger?: number): UseInventoryCountsReturn => {
  const { token } = useAuth();
  const { activeSpace } = useSpace();
  const [counts, setCounts] = useState<InventoryCounts | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCounts = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const spaceParam = activeSpace ? `?space_id=${activeSpace.id}` : '';
      const response = await fetch(`${API_BASE_URL}/api/items/counts${spaceParam}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch counts: ${response.status}`);
      }

      const data = await response.json();
      
      // Update state
      setCounts(data);
      
      // Update cache
      setCachedCounts(data, activeSpace?.id);
      
      console.log('[useInventoryCounts] Fetched and cached counts for space:', activeSpace?.id, data);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch counts';
      setError(errorMsg);
      console.error('[useInventoryCounts] Error fetching counts:', err);
    } finally {
      setLoading(false);
    }
  }, [token, activeSpace]);

  // Load cached counts immediately when space changes
  useEffect(() => {
    const cached = getCachedCounts(activeSpace?.id);
    if (cached) {
      setCounts({
        all: cached.all,
        wandering: cached.wandering,
        shared_with_me: cached.shared_with_me,
        by_collection: cached.by_collection,
      });
      console.log('[useInventoryCounts] Loaded cached counts for space:', activeSpace?.id);
    } else {
      // If no cache for this space, clear current counts to avoid showing stale data from previous space
      setCounts(null);
    }
  }, [activeSpace?.id]);

  // Fetch fresh counts
  useEffect(() => {
    if (token) {
      fetchCounts();
    }
  }, [token, refreshTrigger, fetchCounts, activeSpace?.id]);

  const invalidateCounts = useCallback(() => {
    invalidateCountsCache();
    setCounts(null);
    // Immediately refetch fresh counts
    fetchCounts();
  }, [fetchCounts]);

  return {
    counts,
    loading,
    error,
    refetchCounts: fetchCounts,
    invalidateCounts,
  };
};
